import { useState, useEffect, useRef } from 'react'
import { X, User, MapPin, Package, Truck, Plus, Trash2, Loader2, Save, Search, Building2, CreditCard, RotateCcw } from 'lucide-react'
import { shipping, ManualOrder, ManualOrderRequest, FactusolClient, getFactusolPaymentMethods } from '@/lib/api'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getAddressCountryWarning, inferCountryFromText, countryLabel } from '@/lib/addressCountry'

interface ManualOrderModalProps {
  onClose: () => void
  onSuccess: () => void
  editingOrder?: ManualOrder | null
}

const COUNTRIES = [
  // Principales
  { code: 'ES', name: 'España' },
  { code: 'PT', name: 'Portugal' },
  // Unión Europea (orden alfabético)
  { code: 'AT', name: 'Austria' },
  { code: 'BE', name: 'Bélgica' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'HR', name: 'Croacia' },
  { code: 'CY', name: 'Chipre' },
  { code: 'CZ', name: 'Chequia' },
  { code: 'DK', name: 'Dinamarca' },
  { code: 'EE', name: 'Estonia' },
  { code: 'FI', name: 'Finlandia' },
  { code: 'FR', name: 'Francia' },
  { code: 'DE', name: 'Alemania' },
  { code: 'GR', name: 'Grecia' },
  { code: 'HU', name: 'Hungría' },
  { code: 'IE', name: 'Irlanda' },
  { code: 'IT', name: 'Italia' },
  { code: 'LV', name: 'Letonia' },
  { code: 'LT', name: 'Lituania' },
  { code: 'LU', name: 'Luxemburgo' },
  { code: 'MT', name: 'Malta' },
  { code: 'NL', name: 'Países Bajos' },
  { code: 'PL', name: 'Polonia' },
  { code: 'RO', name: 'Rumanía' },
  { code: 'SK', name: 'Eslovaquia' },
  { code: 'SI', name: 'Eslovenia' },
  { code: 'SE', name: 'Suecia' },
  // Otros europeos
  { code: 'GB', name: 'Reino Unido' },
  { code: 'CH', name: 'Suiza' },
  { code: 'NO', name: 'Noruega' },
  { code: 'AD', name: 'Andorra' },
]

const CARRIERS = [
  { code: '', name: 'Sin asignar' },
  { code: 'mrw', name: 'MRW' },
  { code: 'gls', name: 'GLS' },
  { code: 'ctt', name: 'CTT Express' },
  { code: 'dhl', name: 'DHL Parcel - Domicilio' },
  { code: 'dhl_sp', name: 'DHL Parcel - Service Point' },
  { code: 'cex', name: 'Correos Express' },
]

interface DhlServicePoint {
  id: string
  name: string
  address: string
  city: string
  postal_code: string
  country: string
  hours?: string
}

interface ProductFormData {
  product_name: string
  product_reference: string
  quantity: number
  price: number
  weight: number
}

export default function ManualOrderModal({ onClose, onSuccess, editingOrder }: ManualOrderModalProps) {
  const queryClient = useQueryClient()

  const [formData, setFormData] = useState<ManualOrderRequest>({
    // Destinatario
    recipient_name: editingOrder?.recipient_name || '',
    recipient_email: editingOrder?.recipient_email || '',
    recipient_phone: editingOrder?.recipient_phone || '',
    recipient_nif: editingOrder?.recipient_nif || '',
    recipient_company: editingOrder?.recipient_company || '',
    // Dirección
    address_street: editingOrder?.address_street || '',
    address_city: editingOrder?.address_city || '',
    address_postal_code: editingOrder?.address_postal_code || '',
    address_province: editingOrder?.address_province || '',
    address_country: editingOrder?.address_country || 'ES',
    // Pedido
    custom_reference: editingOrder?.custom_reference || '',
    warehouse: editingOrder?.warehouse || 'Almacén principal',
    payment_method: editingOrder?.payment_method || '',
    invoice_number: editingOrder?.invoice_number || '',
    content_description: editingOrder?.content_description || '',
    total_value: editingOrder?.total_value || 0,
    currency: editingOrder?.currency || 'EUR',
    // Envío
    carrier_code: editingOrder?.carrier_code === 'dhl' && editingOrder?.service_point_id ? 'dhl_sp' : editingOrder?.carrier_code || '',
    carrier_message: editingOrder?.carrier_message || '',
    weight: editingOrder?.weight || 0.5,
    packages: editingOrder?.packages || 1,
    // Opciones de envío
    is_cod: editingOrder?.is_cod || false,
    cod_amount: editingOrder?.cod_amount || 0,
    with_return: editingOrder?.with_return || false,
    // DHL Service Point
    service_point_id: editingOrder?.service_point_id || undefined,
    service_point_name: editingOrder?.service_point_name || undefined,
    service_point_address: editingOrder?.service_point_address || undefined,
    service_point_city: editingOrder?.service_point_city || undefined,
    service_point_postal_code: editingOrder?.service_point_postal_code || undefined,
    service_point_country: editingOrder?.service_point_country || undefined,
  })

  // Productos del pedido manual
  const [products, setProducts] = useState<ProductFormData[]>(
    editingOrder?.products.map(p => ({
      product_name: p.product_name,
      product_reference: p.product_reference || '',
      quantity: p.quantity,
      price: p.price,
      weight: p.weight,
    })) || []
  )

  const [activeSection, setActiveSection] = useState<'recipient' | 'address' | 'order' | 'products' | 'shipping'>('recipient')

  // Query para métodos de pago de Factusol
  const { data: paymentMethods = [] } = useQuery({
    queryKey: ['factusol-payment-methods'],
    queryFn: getFactusolPaymentMethods,
    staleTime: 1000 * 60 * 60, // 1 hora
  })

  // Búsqueda de clientes Factusol
  const [showClientSearch, setShowClientSearch] = useState(false)
  const [clientSearch, setClientSearch] = useState('')
  const [searchResults, setSearchResults] = useState<FactusolClient[]>([])
  const [isSearching, setIsSearching] = useState(false)
  // Aviso sobre el país del cliente cargado desde Factusol (vacío o deducido)
  const [clientCountryNotice, setClientCountryNotice] = useState<string | null>(null)
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // DHL Service Points
  const [dhlPoints, setDhlPoints] = useState<DhlServicePoint[]>([])
  const [isSearchingPoints, setIsSearchingPoints] = useState(false)
  const [dhlSearchError, setDhlSearchError] = useState('')

  // Búsqueda con debounce
  useEffect(() => {
    if (clientSearch.length < 2) {
      setSearchResults([])
      return
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setIsSearching(true)
      try {
        const results = await shipping.searchClients(clientSearch)
        setSearchResults(results)
      } catch (error) {
        console.error('Error buscando clientes:', error)
        setSearchResults([])
      } finally {
        setIsSearching(false)
      }
    }, 300)

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [clientSearch])

  // Seleccionar cliente y rellenar formulario
  const selectClient = (client: FactusolClient) => {
    setFormData({
      ...formData,
      // Destinatario
      recipient_name: client.name,
      recipient_email: client.email || '',
      recipient_phone: client.phone || '',
      recipient_nif: client.nif || '',
      recipient_company: client.company || '',
      // Dirección
      address_street: client.address,
      address_city: client.city,
      address_postal_code: client.postal_code,
      address_province: client.province || '',
      // NUNCA asumir España: si Factusol no tiene país, el usuario debe elegirlo
      address_country: client.country || '',
    })
    if (!client.country) {
      const hint = inferCountryFromText(client.city, client.province)
      setClientCountryNotice(
        hint
          ? `La ficha de Factusol no tiene país. Por la población parece ${countryLabel(hint)}: selecciónalo en "Dirección".`
          : 'La ficha de Factusol no tiene país. Selecciona el país de destino en "Dirección" antes de guardar.'
      )
      setActiveSection('address')
    } else if (client.country_source === 'inferred') {
      setClientCountryNotice(`País deducido de la población de Factusol: ${countryLabel(client.country)}. Comprueba que es correcto.`)
    } else {
      setClientCountryNotice(null)
    }
    setClientSearch('')
    setSearchResults([])
    setShowClientSearch(false)
  }

  // Preparar datos para envío: convertir dhl_sp → dhl + service_point fields
  const prepareFormData = (): ManualOrderRequest => {
    const data = { ...formData }
    if (data.carrier_code === 'dhl_sp') {
      data.carrier_code = 'dhl'
      // service_point fields ya están en formData
    } else {
      // Limpiar campos SP si no es dhl_sp
      data.service_point_id = undefined
      data.service_point_name = undefined
      data.service_point_address = undefined
      data.service_point_city = undefined
      data.service_point_postal_code = undefined
      data.service_point_country = undefined
    }
    return data
  }

  // Buscar DHL service points
  const searchDhlPoints = async () => {
    const postalCode = formData.address_postal_code
    const country = formData.address_country || 'ES'
    if (!postalCode || postalCode.length < 3) {
      setDhlSearchError('Introduce un código postal válido en la sección Dirección')
      return
    }
    setIsSearchingPoints(true)
    setDhlSearchError('')
    setDhlPoints([])
    try {
      const res = await fetch(`/api/shipping/dhl/service-points?country=${country}&postal_code=${encodeURIComponent(postalCode)}&limit=10`)
      const data = await res.json()
      if (data.success && data.points?.length > 0) {
        setDhlPoints(data.points)
      } else {
        setDhlSearchError('No se encontraron puntos de entrega para ese código postal')
      }
    } catch {
      setDhlSearchError('Error al buscar puntos de entrega')
    } finally {
      setIsSearchingPoints(false)
    }
  }

  // Mutations
  const createMutation = useMutation({
    mutationFn: async () => {
      // 1. Crear el pedido
      const order = await shipping.createManualOrder(prepareFormData())

      // 2. Añadir productos si hay
      for (const product of products) {
        await shipping.addManualOrderProduct(order.id, product)
      }

      return order
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['manualOrders'] })
      onSuccess()
    }
  })

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!editingOrder) throw new Error('No order to edit')

      // 1. Actualizar el pedido
      const order = await shipping.updateManualOrder(editingOrder.id, prepareFormData())

      // 2. Limpiar productos existentes y añadir los nuevos
      // (Simplificado: primero eliminamos todos y luego añadimos)
      for (const existingProd of editingOrder.products) {
        await shipping.deleteManualOrderProduct(editingOrder.id, existingProd.id)
      }
      for (const product of products) {
        await shipping.addManualOrderProduct(editingOrder.id, product)
      }

      return order
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['manualOrders'] })
      onSuccess()
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.recipient_name.trim() || !formData.address_street.trim()) return
    if (packageWarning) return // No permitir guardar si hay error de bultos
    if (addressCountryWarning) return // País incoherente con la dirección
    if (formData.carrier_code === 'dhl_sp' && !formData.service_point_id) return // Requiere punto seleccionado

    if (editingOrder) {
      updateMutation.mutate()
    } else {
      createMutation.mutate()
    }
  }

  const addProduct = () => {
    setProducts([...products, {
      product_name: '',
      product_reference: '',
      quantity: 1,
      price: 0,
      weight: 0
    }])
  }

  const removeProduct = (index: number) => {
    setProducts(products.filter((_, i) => i !== index))
  }

  const updateProduct = (index: number, field: keyof ProductFormData, value: string | number) => {
    const updated = [...products]
    updated[index] = { ...updated[index], [field]: value }
    setProducts(updated)
  }

  const isLoading = createMutation.isPending || updateMutation.isPending
  const totalProducts = products.reduce((sum, p) => sum + (p.price * p.quantity), 0)

  // Validar restricción de bultos por transportista y destino
  const getPackageLimitWarning = (): string | null => {
    const carrier = formData.carrier_code?.toLowerCase()
    const country = formData.address_country
    const packages = formData.packages || 1

    // MRW: Solo 1 bulto para Portugal
    if (carrier === 'mrw' && country === 'PT' && packages > 1) {
      return `MRW solo permite 1 bulto para envíos a Portugal`
    }

    // GLS: Solo 1 bulto para envíos internacionales (excepto Portugal)
    if (carrier === 'gls' && country !== 'ES' && country !== 'PT' && packages > 1) {
      return `GLS solo permite 1 bulto para envíos internacionales`
    }

    // DHL Service Point: requiere punto seleccionado
    if (carrier === 'dhl_sp' && !formData.service_point_id) {
      return `Selecciona un punto de recogida DHL`
    }

    return null
  }

  const packageWarning = getPackageLimitWarning()
  const addressCountryWarning = getAddressCountryWarning({
    country: formData.address_country,
    city: formData.address_city,
    province: formData.address_province,
    postal_code: formData.address_postal_code,
  })

  const sections = [
    { id: 'recipient', label: 'Destinatario', icon: User },
    { id: 'address', label: 'Dirección', icon: MapPin },
    { id: 'order', label: 'Pedido', icon: Package },
    { id: 'products', label: 'Productos', icon: Package },
    { id: 'shipping', label: 'Envío', icon: Truck },
  ] as const

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div
        className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            {editingOrder ? `Editar pedido ${editingOrder.reference}` : 'Crear envío manual'}
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSubmit}
              disabled={isLoading || !formData.recipient_name.trim() || !formData.address_street.trim() || !!packageWarning || !!addressCountryWarning || (formData.carrier_code === 'dhl_sp' && !formData.service_point_id)}
              title={addressCountryWarning || packageWarning || undefined}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              <span className="text-sm font-medium">Guardar</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Section tabs */}
        <div className="flex border-b border-slate-200 dark:border-slate-700 px-4 overflow-x-auto">
          {sections.map(section => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeSection === section.id
                  ? 'border-teal-500 text-teal-600 dark:text-teal-400'
                  : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              }`}
            >
              <section.icon className="w-4 h-4" />
              {section.label}
            </button>
          ))}
        </div>

        {/* Form content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          {/* Destinatario */}
          {activeSection === 'recipient' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Nombre completo *
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowClientSearch(true)}
                      className="flex items-center gap-1 text-xs text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 transition-colors"
                    >
                      <Search className="w-3 h-3" />
                      <span>Buscar en Factusol</span>
                    </button>
                  </div>
                  <input
                    type="text"
                    value={formData.recipient_name}
                    onChange={e => setFormData({ ...formData, recipient_name: e.target.value })}
                    placeholder="Nombre y apellidos"
                    required
                    className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.recipient_email || ''}
                    onChange={e => setFormData({ ...formData, recipient_email: e.target.value })}
                    placeholder="email@ejemplo.com"
                    className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    value={formData.recipient_phone || ''}
                    onChange={e => setFormData({ ...formData, recipient_phone: e.target.value })}
                    placeholder="+34 600 000 000"
                    className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    NIF/DNI/CIF
                  </label>
                  <input
                    type="text"
                    value={formData.recipient_nif || ''}
                    onChange={e => setFormData({ ...formData, recipient_nif: e.target.value })}
                    placeholder="12345678A"
                    className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Empresa
                  </label>
                  <input
                    type="text"
                    value={formData.recipient_company || ''}
                    onChange={e => setFormData({ ...formData, recipient_company: e.target.value })}
                    placeholder="Nombre de empresa (opcional)"
                    className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Dirección */}
          {activeSection === 'address' && (
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Dirección *
                </label>
                <input
                  type="text"
                  value={formData.address_street}
                  onChange={e => setFormData({ ...formData, address_street: e.target.value })}
                  placeholder="Calle, número, piso..."
                  required
                  className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Código postal *
                  </label>
                  <input
                    type="text"
                    value={formData.address_postal_code}
                    onChange={e => setFormData({ ...formData, address_postal_code: e.target.value })}
                    placeholder="28001"
                    required
                    className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Ciudad *
                  </label>
                  <input
                    type="text"
                    value={formData.address_city}
                    onChange={e => setFormData({ ...formData, address_city: e.target.value })}
                    placeholder="Madrid"
                    required
                    className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Provincia
                  </label>
                  <input
                    type="text"
                    value={formData.address_province || ''}
                    onChange={e => setFormData({ ...formData, address_province: e.target.value })}
                    placeholder="Madrid"
                    className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  País
                </label>
                <select
                  value={formData.address_country || ''}
                  onChange={e => { setFormData({ ...formData, address_country: e.target.value }); setClientCountryNotice(null) }}
                  className={`w-full mt-1 px-3 py-2 text-sm rounded-lg border bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    addressCountryWarning ? 'border-red-400 dark:border-red-600' : 'border-slate-200 dark:border-slate-600'
                  }`}
                >
                  {!formData.address_country && <option value="">— Selecciona el país —</option>}
                  {COUNTRIES.map(country => (
                    <option key={country.code} value={country.code}>{country.name}</option>
                  ))}
                </select>
              </div>
              {(clientCountryNotice || addressCountryWarning) && (
                <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg space-y-1">
                  {clientCountryNotice && (
                    <p className="text-sm text-red-700 dark:text-red-400">⚠ {clientCountryNotice}</p>
                  )}
                  {addressCountryWarning && (
                    <p className="text-sm text-red-700 dark:text-red-400 font-medium">⚠ {addressCountryWarning}</p>
                  )}
                  <p className="text-xs text-red-600 dark:text-red-500">
                    El país determina el servicio del transportista: con el país mal la etiqueta sale nacional y el paquete se extravía.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Detalles del pedido */}
          {activeSection === 'order' && (
            <div className="space-y-4">
              {/* Referencia del pedido - campo destacado */}
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
                <label className="text-xs font-medium text-blue-700 dark:text-blue-400 uppercase tracking-wider">
                  Referencia del pedido
                </label>
                <input
                  type="text"
                  value={formData.custom_reference || ''}
                  onChange={e => setFormData({ ...formData, custom_reference: e.target.value })}
                  placeholder="Ej: 000123205, PED-2024-001..."
                  className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-blue-300 dark:border-blue-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                  Esta referencia aparecerá en la etiqueta del transportista (REF: ...)
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Almacén
                  </label>
                  <input
                    type="text"
                    value={formData.warehouse || ''}
                    onChange={e => setFormData({ ...formData, warehouse: e.target.value })}
                    placeholder="Almacén principal"
                    className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Método de pago
                  </label>
                  <select
                    value={formData.payment_method || ''}
                    onChange={e => setFormData({ ...formData, payment_method: e.target.value })}
                    className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  >
                    <option value="">Sin especificar</option>
                    {paymentMethods.map(method => (
                      <option key={method.code} value={method.name}>
                        {method.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Nº Factura
                  </label>
                  <input
                    type="text"
                    value={formData.invoice_number || ''}
                    onChange={e => setFormData({ ...formData, invoice_number: e.target.value })}
                    placeholder="FAC-2024-0001"
                    className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Valor total (€)
                  </label>
                  <input
                    type="number"
                    value={formData.total_value || 0}
                    onChange={e => setFormData({ ...formData, total_value: parseFloat(e.target.value) || 0 })}
                    min="0"
                    step="0.01"
                    className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Descripción del contenido (aduanas)
                </label>
                <textarea
                  value={formData.content_description || ''}
                  onChange={e => setFormData({ ...formData, content_description: e.target.value })}
                  placeholder="Descripción del contenido para documentos de aduanas..."
                  rows={2}
                  className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 resize-none"
                />
              </div>
            </div>
          )}

          {/* Productos */}
          {activeSection === 'products' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Añade los productos del envío (opcional)
                </p>
                <button
                  type="button"
                  onClick={addProduct}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400 rounded-lg hover:bg-teal-100 dark:hover:bg-teal-900/40 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Añadir producto
                </button>
              </div>

              {products.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No hay productos añadidos</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Cabeceras de columnas */}
                  <div className="flex items-center gap-3 px-4">
                    <div className="flex-1 grid grid-cols-5 gap-3">
                      <div className="col-span-2">
                        <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Nombre</span>
                      </div>
                      <div>
                        <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Referencia</span>
                      </div>
                      <div>
                        <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Cantidad</span>
                      </div>
                      <div>
                        <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Precio</span>
                      </div>
                    </div>
                    <div className="w-8"></div>{/* Espacio para el botón eliminar */}
                  </div>

                  {products.map((product, index) => (
                    <div key={index} className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                      <div className="flex items-start gap-3">
                        <div className="flex-1 grid grid-cols-5 gap-3">
                          <div className="col-span-2">
                            <input
                              type="text"
                              value={product.product_name}
                              onChange={e => updateProduct(index, 'product_name', e.target.value)}
                              placeholder="Nombre del producto"
                              className="w-full px-2 py-1.5 text-sm rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700"
                            />
                          </div>
                          <div>
                            <input
                              type="text"
                              value={product.product_reference}
                              onChange={e => updateProduct(index, 'product_reference', e.target.value)}
                              placeholder="Ref"
                              className="w-full px-2 py-1.5 text-sm rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700"
                            />
                          </div>
                          <div>
                            <input
                              type="number"
                              value={product.quantity}
                              onChange={e => updateProduct(index, 'quantity', parseInt(e.target.value) || 1)}
                              min="1"
                              className="w-full px-2 py-1.5 text-sm rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700"
                            />
                          </div>
                          <div>
                            <input
                              type="number"
                              value={product.price}
                              onChange={e => updateProduct(index, 'price', parseFloat(e.target.value) || 0)}
                              min="0"
                              step="0.01"
                              placeholder="€"
                              className="w-full px-2 py-1.5 text-sm rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700"
                            />
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeProduct(index)}
                          className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                  <div className="text-right text-sm text-slate-600 dark:text-slate-400">
                    Total productos: <span className="font-semibold">€{totalProducts.toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Envío */}
          {activeSection === 'shipping' && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Transportista
                  </label>
                  <select
                    value={formData.carrier_code || ''}
                    onChange={e => {
                      const newCarrier = e.target.value
                      const updates: Partial<ManualOrderRequest> = { carrier_code: newCarrier }
                      // Limpiar SP si cambia de dhl_sp a otro
                      if (newCarrier !== 'dhl_sp') {
                        updates.service_point_id = undefined
                        updates.service_point_name = undefined
                        updates.service_point_address = undefined
                        updates.service_point_city = undefined
                        updates.service_point_postal_code = undefined
                        updates.service_point_country = undefined
                        setDhlPoints([])
                        setDhlSearchError('')
                      }
                      setFormData({ ...formData, ...updates })
                    }}
                    className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  >
                    {CARRIERS.map(carrier => (
                      <option key={carrier.code} value={carrier.code}>{carrier.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Peso (kg)
                  </label>
                  <input
                    type="number"
                    value={formData.weight || 0.5}
                    onChange={e => setFormData({ ...formData, weight: parseFloat(e.target.value) || 0.5 })}
                    min="0.1"
                    step="0.1"
                    className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Bultos
                  </label>
                  <input
                    type="number"
                    value={formData.packages || 1}
                    onChange={e => setFormData({ ...formData, packages: parseInt(e.target.value) || 1 })}
                    min="1"
                    className={`w-full mt-1 px-3 py-2 text-sm rounded-lg border bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                      packageWarning
                        ? 'border-red-500 dark:border-red-500'
                        : 'border-slate-200 dark:border-slate-600'
                    }`}
                  />
                </div>
              </div>
              {/* Advertencia de país incoherente con la dirección */}
              {addressCountryWarning && (
                <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg">
                  <p className="text-sm text-red-700 dark:text-red-400 font-medium">⚠ {addressCountryWarning}</p>
                  <p className="text-xs text-red-600 dark:text-red-500 mt-1">Corrígelo en la sección "Dirección".</p>
                </div>
              )}

              {/* Advertencia de límite de bultos */}
              {packageWarning && (
                <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg">
                  <p className="text-sm text-red-700 dark:text-red-400 font-medium">
                    ⚠ {packageWarning}
                  </p>
                  <p className="text-xs text-red-600 dark:text-red-500 mt-1">
                    Reduce a 1 bulto o cambia de transportista.
                  </p>
                </div>
              )}

              {/* DHL Service Point selector */}
              {formData.carrier_code === 'dhl_sp' && (
                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                      <span className="text-sm font-medium text-yellow-800 dark:text-yellow-300">Punto de recogida DHL</span>
                    </div>
                    <button
                      type="button"
                      onClick={searchDhlPoints}
                      disabled={isSearchingPoints}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-yellow-100 dark:bg-yellow-800/30 text-yellow-800 dark:text-yellow-300 border border-yellow-300 dark:border-yellow-600 rounded-lg hover:bg-yellow-200 dark:hover:bg-yellow-800/50 transition-colors disabled:opacity-50"
                    >
                      {isSearchingPoints ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                      Buscar puntos
                    </button>
                  </div>

                  {/* Punto seleccionado */}
                  {formData.service_point_id && (
                    <div className="p-3 bg-white dark:bg-slate-700 rounded-lg border border-yellow-300 dark:border-yellow-600">
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{formData.service_point_name}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        {formData.service_point_address}, {formData.service_point_postal_code} {formData.service_point_city}
                      </p>
                      <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">ID: {formData.service_point_id}</p>
                    </div>
                  )}

                  {/* Error */}
                  {dhlSearchError && (
                    <p className="text-xs text-red-600 dark:text-red-400">{dhlSearchError}</p>
                  )}

                  {/* Resultados búsqueda */}
                  {dhlPoints.length > 0 && (
                    <div className="max-h-48 overflow-y-auto space-y-1.5">
                      {dhlPoints.map(point => (
                        <button
                          key={point.id}
                          type="button"
                          onClick={() => {
                            setFormData({
                              ...formData,
                              service_point_id: point.id,
                              service_point_name: point.name,
                              service_point_address: point.address,
                              service_point_city: point.city,
                              service_point_postal_code: point.postal_code,
                              service_point_country: point.country,
                            })
                            setDhlPoints([])
                          }}
                          className={`w-full p-2.5 text-left rounded-lg border transition-colors ${
                            formData.service_point_id === point.id
                              ? 'bg-yellow-100 dark:bg-yellow-800/30 border-yellow-400'
                              : 'bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600 hover:border-yellow-300 dark:hover:border-yellow-600'
                          }`}
                        >
                          <p className="text-sm font-medium text-slate-900 dark:text-white">{point.name}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            {point.address}, {point.postal_code} {point.city}
                          </p>
                          {point.hours && (
                            <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{point.hours}</p>
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  {!formData.service_point_id && !dhlSearchError && dhlPoints.length === 0 && (
                    <p className="text-xs text-yellow-700 dark:text-yellow-400">
                      Rellena la dirección y pulsa "Buscar puntos" para seleccionar un punto de recogida.
                    </p>
                  )}
                </div>
              )}

              {/* Contrareembolso (COD) */}
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_cod || false}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        is_cod: e.target.checked,
                        cod_amount: e.target.checked ? formData.cod_amount : 0
                      })
                    }}
                    className="w-4 h-4 text-amber-600 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-500 rounded focus:ring-amber-500 focus:ring-2"
                  />
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                    <span className="text-sm font-medium text-amber-800 dark:text-amber-300">Contrareembolso</span>
                  </div>
                </label>
                {formData.is_cod && (
                  <div className="mt-3 ml-7">
                    <label className="text-xs font-medium text-amber-700 dark:text-amber-400 uppercase tracking-wider">
                      Importe a cobrar
                    </label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-600 dark:text-amber-400 font-medium">€</span>
                      <input
                        type="number"
                        value={formData.cod_amount || ''}
                        onChange={e => setFormData({ ...formData, cod_amount: parseFloat(e.target.value) || 0 })}
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-amber-300 dark:border-amber-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Envío con retorno */}
              <label className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
                <input
                  type="checkbox"
                  checked={formData.with_return || false}
                  onChange={(e) => setFormData({ ...formData, with_return: e.target.checked })}
                  className="w-4 h-4 text-teal-600 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-500 rounded focus:ring-teal-500 focus:ring-2"
                />
                <div className="flex items-center gap-2">
                  <RotateCcw className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                  <div>
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Con retorno</span>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      El mensajero lleva etiqueta de retorno
                    </p>
                  </div>
                </div>
              </label>

              <div>
                <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Mensaje para el transportista
                </label>
                <textarea
                  value={formData.carrier_message || ''}
                  onChange={e => setFormData({ ...formData, carrier_message: e.target.value })}
                  placeholder="Instrucciones especiales de entrega..."
                  rows={3}
                  className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 resize-none"
                />
              </div>
            </div>
          )}
        </form>

        {/* Footer con navegación */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
          <div className="text-sm text-slate-500">
            {activeSection === 'recipient' && 'Paso 1 de 5'}
            {activeSection === 'address' && 'Paso 2 de 5'}
            {activeSection === 'order' && 'Paso 3 de 5'}
            {activeSection === 'products' && 'Paso 4 de 5'}
            {activeSection === 'shipping' && 'Paso 5 de 5'}
          </div>
          <div className="flex gap-2">
            {activeSection !== 'recipient' && (
              <button
                type="button"
                onClick={() => {
                  const idx = sections.findIndex(s => s.id === activeSection)
                  if (idx > 0) setActiveSection(sections[idx - 1].id)
                }}
                className="px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                Anterior
              </button>
            )}
            {activeSection !== 'shipping' && (
              <button
                type="button"
                onClick={() => {
                  const idx = sections.findIndex(s => s.id === activeSection)
                  if (idx < sections.length - 1) setActiveSection(sections[idx + 1].id)
                }}
                className="px-4 py-2 text-sm bg-slate-900 dark:bg-slate-600 text-white rounded-lg hover:bg-slate-800 dark:hover:bg-slate-500 transition-colors"
              >
                Siguiente
              </button>
            )}
          </div>
        </div>

        {/* Error */}
        {(createMutation.error || updateMutation.error) && (
          <div className="px-6 pb-4">
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">
                Error: {String((createMutation.error || updateMutation.error) as any)}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Modal de búsqueda de clientes Factusol */}
      {showClientSearch && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-[70]"
        >
          <div
            className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
              <h4 className="text-lg font-semibold text-slate-900 dark:text-white">
                Buscar cliente en Factusol
              </h4>
              <button
                onClick={() => setShowClientSearch(false)}
                className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Campo de búsqueda */}
            <div className="p-5">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={clientSearch}
                  onChange={e => setClientSearch(e.target.value)}
                  placeholder="Buscar por nombre, NIF, email o teléfono..."
                  autoFocus
                  className="w-full pl-10 pr-10 py-3 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
                {isSearching && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />
                )}
              </div>

              {/* Resultados */}
              <div className="mt-4 max-h-80 overflow-y-auto">
                {searchResults.length > 0 ? (
                  <div className="space-y-2">
                    {searchResults.map(client => (
                      <button
                        key={client.code}
                        type="button"
                        onClick={() => selectClient(client)}
                        className="w-full p-4 text-left bg-slate-50 dark:bg-slate-700/50 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg border border-transparent hover:border-teal-200 dark:hover:border-teal-800 transition-colors"
                      >
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
                            <Building2 className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm text-slate-900 dark:text-white">
                              {client.name}
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 flex flex-wrap gap-x-3">
                              {client.nif && <span>{client.nif}</span>}
                              {client.phone && <span>{client.phone}</span>}
                              {client.email && <span>{client.email}</span>}
                            </div>
                            <div className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                              {client.address}, {client.postal_code} {client.city}
                              {' · '}
                              {client.country ? (
                                <span className={client.country_source === 'inferred' ? 'text-amber-600 dark:text-amber-400' : ''}>
                                  {countryLabel(client.country)}{client.country_source === 'inferred' ? ' (deducido)' : ''}
                                </span>
                              ) : (
                                <span className="text-red-600 dark:text-red-400 font-medium">sin país en Factusol</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : clientSearch.length >= 2 && !isSearching ? (
                  <div className="text-center py-8 text-slate-400">
                    <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No se encontraron clientes</p>
                  </div>
                ) : clientSearch.length < 2 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Building2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Escribe al menos 2 caracteres para buscar</p>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
